import React, { useMemo, useEffect } from 'react';
import { Formik } from 'formik';
import Scroll from 'react-scroll';

import BasicButtonGroup from 'components/BasicButtonGroup';
import BasicButton from 'components/BasicButton';
import BasicSubmitButton from 'components/BasicSubmitButton';
import LoadMask from 'components/LoadMask';

import { CSSTransition } from 'react-transition-group';
import { ANIMATION_TIMEOUT_APPEAR } from 'constants/animation.js';

export const MultistepFormPage = props => {
  const { children, parentState } = props;
  const { isValid, errors, isSubmitting } = parentState;

  useEffect(() => {
    if (isValid || !isSubmitting) return;
    if (Object.keys(errors).length) {
      Scroll.scroller.scrollTo('form__error', {
        duration: 300,
        delay: 50,
        smooth: true,
        offset: -150,
      });
    }
  }, [isValid, errors, isSubmitting]);
  return children(parentState);
};

const MultistepForm = props => {
  const {
    page = 0,
    options,
    params,
    children,
    initialValues,
    validationSchema,
    onChangeStep,
    onSubmit,
    getAsyncOptions,
  } = props;

  const [activePage, isLastPage] = useMemo(
    () => [
      React.Children.toArray(children)[page],
      page === React.Children.count(children) - 1,
    ],
    [page, options]
  );

  const next = values => {
    onChangeStep(page + 1, values);
  };

  const previous = () => {
    onChangeStep(page - 1);
  };

  const getSchema = () => {
    return validationSchema && validationSchema[page]
      ? validationSchema[page]
      : null;
  };

  const handleSubmit = (values, bag) => {
    const isLastPage = page === React.Children.count(children) - 1;
    if (isLastPage) {
      return onSubmit(values, bag);
    } else {
      bag.setSubmitting(false);
      bag.setTouched({});
      next(values, bag);
    }
  };

  return (
    <CSSTransition
      in={true}
      appear={true}
      timeout={ANIMATION_TIMEOUT_APPEAR}
      classNames="fade"
    >
      <Formik
        initialValues={initialValues}
        enableReinitialize={true}
        validationSchema={getSchema}
        onSubmit={handleSubmit}
        render={({
          values,
          errors,
          touched,
          dirty,
          handleChange,
          handleBlur,
          handleSubmit,
          handleReset,
          isValid,
          isSubmitting,
          setFieldValue,
          setFieldError,
          setFieldTouched,
        }) => (
          <form onSubmit={handleSubmit} className="form__content">
            {isSubmitting && <LoadMask />}
            {React.cloneElement(activePage, {
              parentState: {
                values,
                options,
                params,
                errors,
                touched,
                dirty,
                handleChange,
                handleBlur,
                handleSubmit,
                handleReset,
                isSubmitting,
                setFieldValue,
                setFieldError,
                setFieldTouched,
                getAsyncOptions,
              },
            })}
            <div className="form__controls">
              <BasicButtonGroup>
                {page > 0 && (
                  <BasicButton
                    controlFunc={previous}
                    btnWidth={200}
                    btnHeight={40}
                    btnFontWeight={500}
                    btnFontSize="0.8571rem"
                    btnArrow={false}
                    btnTextColor="#27282d"
                    btnText="Back"
                  />
                )}
                {!isLastPage && (
                  <BasicSubmitButton
                    controlFunc={handleSubmit}
                    btnWidth={200}
                    btnHeight={40}
                    btnFontWeight={500}
                    btnFontSize="0.8571rem"
                    btnArrow={true}
                    btnTextColor="#27282d"
                    btnText="Continue"
                  />
                )}
                {isLastPage && (
                  <BasicSubmitButton
                    controlFunc={handleSubmit}
                    btnWidth={200}
                    btnHeight={40}
                    btnFontWeight={500}
                    btnFontSize="0.8571rem"
                    btnArrow={true}
                    btnTextColor="#27282d"
                    btnText="Submit Application"
                    disabled={isSubmitting}
                  />
                )}
              </BasicButtonGroup>
            </div>
          </form>
        )}
      />
    </CSSTransition>
  );
};

export default MultistepForm;
